import { createConnections } from './chat/connection';
import { useChatInbox } from './useChatInbox';
import { blankView, keepNewest, withReaction, type Link } from './chat/model';
import type { ChatApi, ChatOptions, ChatRow, RoomView } from './chat/types';
export type { ChatApi, ChatOptions, ChatRow, RoomView, ChatStatus, InboxMention } from './chat/types';
export type { InboxState } from './useChatInbox';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  LIMITS,
  PROTOCOL_VERSION,
  accountName,
  cleanFace,
  cleanQuery,
  cleanTag,
  cleanTags,
  cleanText,
  levelRoom,
  searchRule,
  snippetOf,
  type ChatImage,
  type ClientMessage,
  type ErrCode,
  type RoomId,
  type ServerMessage,
} from '@engineer-os/chat';
import {
  chatPrefs,
  clearLink,
  fetchOccupancy,
  linkedMessage,
  looksFresh,
  makeNonce,
  missionLevel,
  recoverPrefs,
  roomFor,
  updatePrefs,
  withRecent,
  type Occupancy,
} from '../lib/chat';
import { canNotify, notifyMention } from '../lib/inbox';
import { adoptName, hereName, onName, rename } from '../lib/naming';
import { onOperator, wearFace, wearingOnWire, worn } from '../lib/operator';
import type { Progress } from '../progress';
import { getLocale } from '../lib/locale';
import { sound } from '../lib/sound';
import { getStrings } from '../lib/strings';

/** The arrival tick plays at most this often, across every room, so a busy tab is a murmur, not a rattle. */
const TICK_GAP_MS = 2_000;
/**
 * The longest the first connection waits for a recovered identity. Not a race
 * with the vault — that gives up on its own well inside this — but a stop for
 * the one failure it cannot cover: a storage API that neither resolves nor
 * rejects. Connecting late under a fresh id is recoverable; never connecting
 * at all is not.
 */
const RECOVERY_MAX_MS = 5_000;
/** How long a navigated-to message stays flagged for the flash before it is let go. */
const HIGHLIGHT_MS = 4_000;
/** The occupancy answer is cached by the Worker for 20s, so asking faster than this learns nothing. */
const OCCUPANCY_GAP_MS = 15_000;

/**
 * What to put in front of a learner when the room refuses something.
 *
 * The `message` on an `err` frame is the Worker's, and the Worker writes
 * English: it does not know what language this tab is in, and one sentence on
 * the wire is the right answer for a protocol error. But two of these are not
 * protocol errors — a name that does not meet the rule and text that does not
 * are ordinary human mistakes, and they have the same sentence in this tab's
 * catalogue, asked for in the reader's own language. Anything else is a bug
 * rather than a mistake, and the server's own words are the most useful thing
 * to report.
 *
 * `getStrings` and not `useStrings`: this is read at the instant a toast goes
 * up rather than during a render, so there is nothing to re-render and the
 * language it wants is whatever the page is in at that moment.
 *
 * The third one, a query that is too short or too long, goes to the protocol
 * rather than to the catalogue. It is the same answer by a shorter route: the
 * catalogue's two entries above are literally `nameRule(locale)` and
 * `textRule(locale)`, because the rule belongs to the wire and the tab must
 * never promise something the server then refuses. A third table entry that
 * forwarded to `searchRule` would be a third place for the same sentence to
 * be got wrong.
 */
function reason(code: ErrCode, message: string): string {
  const s = getStrings().chat;
  if (code === 'bad-name') return s.rules.name();
  if (code === 'bad-text') return s.rules.text();
  if (code === 'bad-search') return searchRule(getLocale());
  // Not a rule about what a message may look like, so it is the catalogue's
  // rather than the protocol's: the wire has nothing to check here, only the
  // room does, and what the reader needs is the sentence in their own
  // language rather than the one the Worker had to write in English.
  if (code === 'not-yours') return s.rules.notYours;
  return message;
}

/** A local id for a row the server has not numbered yet. Negative, so it never collides and never pages. */
let tempSeq = 0;
const tempId = () => {
  tempSeq -= 1;
  return tempSeq;
};

export function useChat({ open, rank, level, notify, account, progress, objective = null }: ChatOptions): ChatApi {
  /**
   * The name this browser picked, from the store that owns it (lib/naming.ts).
   *
   * `useSyncExternalStore` rather than state, for the reason the operator
   * below is: this hook is no longer the only place it is set. The profile
   * screen renames too, and it is a different branch of the tree with a router
   * between it and here.
   */
  const storedName = useSyncExternalStore(onName, hereName, hereName);
  // The account's name wins while there is one; the stored name waits
  // underneath for a sign-out, so nobody is asked twice.
  const accountId = account?.id ?? null;
  const verified = account !== null;
  const name = account ? accountName(account.name) : storedName;
  // The face this browser picked, or null for the one the room derives from
  // the tag. Not a dependency of the connection: it rides along in `hello`
  // and changes with `me`, the way the name does.
  const [face, setFaceState] = useState<string | null>(() => chatPrefs().face);
  /**
   * The operator, from the store that owns it (lib/operator.ts).
   *
   * `useSyncExternalStore` rather than state, because this hook is not where
   * it is set: the profile screen is, and that screen is a different branch of
   * the tree with a router between it and here. Subscribing means a figure
   * saved over there reaches every open socket without anything being threaded
   * through App.
   */
  const operator = useSyncExternalStore(onOperator, worn, worn);
  /**
   * A message named in the address bar, read once at the first render.
   *
   * Somebody arrived on a link to one thing that was said. That room is home
   * for this visit — not pinned, not written to the preferences, just where
   * the tab opens — because the alternative is landing in room 1 and being
   * moved somewhere else a beat later, which reads as the page changing its
   * mind. It is read before the preferences are used for anything, and the
   * link is taken back out of the URL in the effect below.
   */
  const linked = useRef(linkedMessage());
  // The room picked by hand, and whether the level is allowed to add its own
  // beside it. Neither of these is "the room this tab is in" any more: what
  // the tab is in is every entry in `links`, and which one is on screen is
  // `activeRoom` below.
  const [chosen, setChosen] = useState<RoomId>(() => linked.current?.room ?? chatPrefs().room);
  const [follow, setFollow] = useState(() => chatPrefs().follow);
  const [recent, setRecent] = useState<RoomId[]>(() => chatPrefs().recent);
  // The id `hello` will carry. It is state, and a dependency of the connection
  // below, so that the one thing that can change it — a recovered identity —
  // reconnects rather than leaves a socket open under a tag that is no longer
  // this learner's. For every other browser it is set once and never moves.
  const [identity, setIdentity] = useState(() => chatPrefs().id);

  /** The snapshot of every open room, home first. What the panel renders. */
  const [views, setViews] = useState<RoomView[]>([]);
  const [activeRoom, setActiveRoom] = useState<RoomId | null>(null);
  const [occupancy, setOccupancy] = useState<Record<string, Occupancy>>({});
  // Read from the inbox poll, which runs from a timer and would otherwise
  // close over whatever the occupancy was when the effect was created.
  const occupancyRef = useRef(occupancy);
  occupancyRef.current = occupancy;
  const [searching, setSearching] = useState<ChatApi['searching']>(null);
  const [highlight, setHighlight] = useState<number | null>(null);
  const [blocked, setBlocked] = useState<string[]>(() => chatPrefs().blocked);

  /* ── The identity, before the first hello ────────────────────────────── */

  // A browser that arrives with no chat identity has not necessarily lost one:
  // localStorage goes on its own far more often than the vault's spare copies
  // do, and recoverPrefs adopts the newest copy that survived. It has to finish
  // before `hello` goes out, because the tag the room shows is a hash of the id
  // in that frame — connect first and adopt afterwards and the learner spends
  // their first session under a tag they are about to abandon, which is the
  // whole problem this was meant to fix.
  //
  // The wait costs the common case nothing. looksFresh() is a synchronous read
  // of the same page load chatPrefs() already did: for everyone who still has
  // an id it is false, no promise is ever created, and the connection effect
  // sees a constant. The browsers that do wait are by definition the ones with
  // nothing in storage, so they have no name, no room of their own and no
  // history to be late for: what the wait delays is a listening socket in room
  // one, and the learner is still reading the level list while it happens.
  const [recovering, setRecovering] = useState(looksFresh);
  useEffect(() => {
    if (!recovering) return;
    let alive = true;
    const settle = () => {
      if (!alive) return;
      // The preferences, not the returned flag: the id, name, room and recent
      // list were all seeded from storage at the first render, and whatever
      // recovery settled on is what they should be now. Unchanged values make
      // every one of these a no-op, which is the deadline's case exactly.
      const p = chatPrefs();
      setIdentity(p.id);
      // The name is already in the preferences this read; what is missing is
      // that the store was never told, and the store is what this hook and the
      // profile screen both read (lib/naming.ts).
      adoptName();
      setFaceState(p.face);
      // Except the room, when somebody arrived on a link: the recovered blob
      // says where they usually sit and the URL says where they were sent,
      // and being sent somewhere is the more recent instruction.
      if (!linked.current) setChosen(p.room);
      setFollow(p.follow);
      setRecent(p.recent);
      setBlocked(p.blocked);
      adoptNotificationPreference(p.notifyMentions);
      setRecovering(false);
    };
    void recoverPrefs().then(settle);
    const deadline = window.setTimeout(settle, RECOVERY_MAX_MS);
    return () => {
      alive = false;
      window.clearTimeout(deadline);
    };
    // Once per mount. recoverPrefs is itself one pass per page load, so a
    // remount in development reads the mirrors once and answers twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A socket is wanted as soon as this browser knows who it is. Not having a
  // name is not a reason to wait: that is what listening is for.
  const wanted = !recovering;

  /* ── What the sockets read ───────────────────────────────────────────── */

  // Handlers on a socket outlive many renders. Everything they read goes
  // through a ref or through the mutable `Link` they were given, so the
  // connection effect depends only on what actually requires new sockets, and
  // so a handler installed ten renders ago never reads a value from that
  // render.
  const links = useRef(new Map<RoomId, Link>());
  const order = useRef<RoomId[]>([]);
  const activeRef = useRef<RoomId | null>(null);
  activeRef.current = activeRoom;
  const openRef = useRef(open);
  openRef.current = open;
  /**
   * What the room is told this browser is wearing: the operator if one was
   * built, then the face if one was picked, then the figure this id gives —
   * or null, which leaves a signed-in person's account picture where it is
   * (lib/operator.ts, `wearingOnWire`).
   *
   * The derived rung is why this is computed here rather than left to the
   * room. The room's own fallback is an emoji face from the tag, which is what
   * a tab running an older bundle gets; a tab that knows about operators names
   * the figure explicitly, so that a room is a room of people from this system
   * rather than half a room of animals.
   *
   * It reads the store directly rather than taking `operator` as an argument,
   * and the subscription above is what makes that safe: a figure saved on the
   * profile screen re-renders this hook, and this line is recomputed with it.
   */
  const wearing = wearingOnWire(account?.id ?? identity, face, account?.picture ?? null);
  const meRef = useRef({ name, face, wearing, rank, level, objective });
  meRef.current = { name, face, wearing, rank, level, objective };
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;
  const { inbox, pullInbox, refreshSoon, markInboxSeen, notifyMentions, setNotifyMentions, adoptNotificationPreference } = useChatInbox(accountId, {
    rooms: () => [...links.current.keys()], occupancy: () => occupancyRef.current,
  });
  const notifyMentionsRef = useRef(notifyMentions);
  notifyMentionsRef.current = notifyMentions;
  const verifiedRef = useRef(verified);
  verifiedRef.current = verified;
  const lastTick = useRef(0);
  /** The query this tab is waiting on, and the room it asked. A `found` for anything else is stale. */
  const searchRef = useRef<{ room: RoomId; q: string } | null>(null);
  /** A permalink into a room this tab is not in yet; picked up when that room's link is made.
   *  Seeded from the address bar, which is the one case where it is set before any room exists. */
  const pendingGoTo = useRef<{ room: RoomId; id: number } | null>(linked.current);
  /** A cleared level waiting for a socket to say it on. */
  const pendingClear = useRef<{ level: number; stars: number } | null>(null);

  /* ── The snapshot ────────────────────────────────────────────────────── */

  /** Rebuild the array the panel renders from the links, in the order home first. */
  const publish = useCallback(() => {
    const map = links.current;
    const next: RoomView[] = [];
    for (const id of order.current) {
      const link = map.get(id);
      if (link) next.push(link.view);
    }
    setViews(next);
  }, []);

  /** Change part of one room and republish. The only way a `RoomView` ever changes. */
  const patch = useCallback(
    (link: Link, next: Partial<RoomView>) => {
      link.view = { ...link.view, ...next };
      publish();
    },
    [publish],
  );

  const sendOn = useCallback((link: Link, msg: ClientMessage): boolean => {
    const s = link.ws;
    if (!s || s.readyState !== WebSocket.OPEN) return false;
    s.send(JSON.stringify(msg));
    return true;
  }, []);

  const fail = useCallback(
    (link: Link, nonce: string) => {
      patch(link, {
        messages: link.view.messages.map((r) => (r.nonce === nonce && r.pending ? { ...r, pending: false, failed: true } : r)),
      });
    },
    [patch],
  );

  /** The link the panel is showing, or the home link, or none at all. */
  const current = useCallback((): Link | null => {
    const map = links.current;
    const id = activeRef.current;
    return (id && map.get(id)) || (order.current[0] ? (map.get(order.current[0]) ?? null) : null);
  }, []);

  /* ── Saying a cleared level out loud ─────────────────────────────────── */

  /**
   * Put the pending clear on a socket, if there is one to put it on.
   *
   * The level's own room if this tab is holding it, because that is where the
   * people working on that level are and the line means the most; the home
   * room otherwise, because a cleared level said into the room somebody is
   * actually sitting in is still worth saying. If neither is open the clear
   * stays pending and the next welcome tries again.
   *
   * **The game does not call the Worker.** This rides a socket that is already
   * open, and the line that keeps the game loop out of the network is drawn on
   * purpose (docs/architecture.md): bash, git, grading and progress are in the
   * page, and the one server-side component is the chat. An HTTP call from the
   * grader to announce a clear would be the first crack in that, and the
   * second would be the retry logic behind it. The server keeps one of these
   * per level per tag per room for ever, so a resend after a reconnect costs
   * nothing, which is exactly why this is allowed to be as dumb as it is.
   */
  const flushClear = useCallback(() => {
    const want = pendingClear.current;
    if (!want || !verifiedRef.current) return;
    const room = levelRoom(want.level);
    const link = (room && links.current.get(room)) || (order.current[0] ? links.current.get(order.current[0]) : undefined);
    if (!link) return;
    // No nonce. There is no optimistic row to reconcile — the event arrives
    // as a `msg` like any other — and a nonce would turn a refusal from a
    // socket the room has not verified into a toast at somebody who did not
    // type anything.
    if (sendOn(link, { t: 'event', kind: 'cleared', level: want.level, stars: want.stars })) pendingClear.current = null;
  }, [sendOn]);

  /* ── The connection ──────────────────────────────────────────────────── */

  const tick = () => {
    const now = Date.now();
    if (now - lastTick.current < TICK_GAP_MS) return;
    lastTick.current = now;
    sound.message();
  };

  /** Zero a room's unread and tell the server how far it has been read. */
  const readNow = (link: Link) => {
    // Both counters, together. The mention count is a subset of the unread
    // one, so a read that cleared the first and left the second would leave
    // the strip claiming somebody is waiting on an answer in a room the
    // learner has just finished reading.
    if (link.view.unread !== 0 || link.view.mentions !== 0) patch(link, { unread: 0, mentions: 0 });
    let newest = 0;
    for (const r of link.view.messages) if (r.id > newest) newest = r.id;
    // Ignored by the server from a socket it cannot name, which is most of
    // them, and sent anyway rather than guarded: the tab does not know
    // whether the Worker could name it, and the frame is eleven bytes.
    if (newest > 0) sendOn(link, { t: 'read', id: newest });
  };

  const handle = (link: Link, m: ServerMessage) => {
    switch (m.t) {
      case 'welcome': {
        link.attempt = 0;
        patch(link, {
          you: m.you,
          members: m.members,
          hasEarlier: m.hasEarlier,
          full: false,
          banned: null,
          // Absent from a server one release behind, which reads as none.
          pins: m.pins ?? [],
          status: 'open',
          // The server's history replaces ours, except for rows that never
          // reached it: those are the learner's own words, kept at the end
          // so the retry is still there to press.
          ...keepNewest([...m.messages, ...link.view.messages.filter((r) => r.failed)]),
        });
        // `hello` has no objective field — it is a `me` thing, because it
        // changes several times inside one level and the connection does not
        // — so a socket that reconnects mid-level has to say it again here or
        // the room forgets which objective this person is on.
        if (meRef.current.objective !== null) sendOn(link, { t: 'me', objective: meRef.current.objective });
        // A permalink that arrived before this room had a socket.
        if (link.wantContext !== null && sendOn(link, { t: 'context', id: link.wantContext })) link.wantContext = null;
        // A clear that happened while nothing was connected, or on the other
        // room. Free to resend: the server keeps one per level per tag.
        flushClear();
        if (openRef.current && activeRef.current === link.room && document.visibilityState === 'visible') readNow(link);
        return;
      }
      case 'you': {
        // The answer to a `me`. For a listener that has just picked a name
        // it is the first time this tab learns its own tag, and the thing
        // that lets it post: `send` refuses while `you` is null. It is also
        // what earns the second socket — see rule 4 — because the room
        // saying who you are is the room saying you may speak.
        patch(link, { you: m.you });
        return;
      }
      case 'msg': {
        const mine = m.m.tag === link.view.you?.tag;
        const rows = link.view.messages;
        if (!rows.some((r) => r.id === m.m.id)) {
          // The echo of our own `say` takes the pending row's place. The
          // nonce stays on the row so its key, and therefore its DOM, does.
          const rest = m.nonce ? rows.filter((r) => r.nonce !== m.nonce) : rows;
          patch(link, keepNewest([...rest, m.nonce ? { ...m.m, nonce: m.nonce } : m.m]));
        }
        if (mine) return;
        // A blocked tag is a message that arrived and will be drawn collapsed,
        // and it is emphatically not news: no count, no sound, no
        // notification. Blocking somebody who then has to be un-counted by
        // hand would be a block that did not block anything.
        if (blockedRef.current.includes(m.m.tag)) return;
        const shut = !openRef.current;
        const hidden = document.visibilityState === 'hidden';
        const you = link.view.you;
        // Being spoken to by name, decided once and used three times below.
        const forMe = you !== null && m.m.mentions?.includes(you.tag) === true;
        // Unread is per room and accrues on both sockets live, which is the
        // whole reason the second one is worth holding: the room you are not
        // looking at is the one you need told about. The mention count rides
        // the same condition, because a mention you are already reading is
        // not waiting for you.
        if (shut || hidden || activeRef.current !== link.room) {
          patch(
            link,
            forMe
              ? { unread: link.view.unread + 1, mentions: link.view.mentions + 1 }
              : { unread: link.view.unread + 1 },
          );
        }
        // The badge is for everyone; the sound is only for people who have
        // joined. A learner who has never opened the panel is listening
        // without knowing it, and a noise from a room they have never
        // heard of is an interruption they cannot account for. The count
        // on the bubble is the quiet way to tell them it is there.
        if (shut && meRef.current.name !== null) tick();
        if (forMe) {
          // Being spoken to is different news from something being said, so
          // it gets the one interruption this product has — and only while
          // the reader is not already looking at the room it happened in.
          if ((shut || hidden) && notifyMentionsRef.current && canNotify()) {
            notifyMention({ fromName: m.m.name, room: link.room, text: m.m.text }, getLocale());
          }
          // The durable row behind it is written by the Worker against the
          // account; ask for it in a moment rather than inventing a local one
          // with an id the server has never heard of.
          if (verifiedRef.current) refreshSoon();
        }
        return;
      }
      case 'members':
        patch(link, { members: m.members });
        return;
      case 'earlier': {
        if (m.messages.length === 0) {
          patch(link, { loadingEarlier: false, hasEarlier: m.hasEarlier });
          return;
        }
        // Prepended pages are not capped: the learner asked for them, and
        // dropping the newest rows to make room would hide the conversation
        // they scrolled up from.
        const have = new Set(link.view.messages.map((r) => r.id));
        const fresh = m.messages.filter((x) => !have.has(x.id));
        patch(link, {
          loadingEarlier: false,
          hasEarlier: m.hasEarlier,
          messages: fresh.length ? [...fresh, ...link.view.messages] : link.view.messages,
        });
        return;
      }
      case 'reaction': {
        const rows = withReaction(link.view.messages, m.id, m.emoji, m.tags);
        if (rows !== link.view.messages) patch(link, { messages: rows });
        return;
      }
      case 'pins': {
        // The whole list, on every change. The rows in the conversation carry
        // their own `pinned` flag and are reconciled against it here, so the
        // marker in the list and the bar above it can never disagree — they
        // are two readings of one frame rather than two pieces of state.
        const ids = new Set(m.pins.map((p) => p.id));
        patch(link, {
          pins: m.pins,
          messages: link.view.messages.map((r) => (ids.has(r.id) === (r.pinned === true) ? r : { ...r, pinned: ids.has(r.id) })),
        });
        return;
      }
      case 'found': {
        const want = searchRef.current;
        if (!want || want.room !== link.room || want.q !== m.q) return;
        setSearching({ q: m.q, results: m.messages, loading: false });
        return;
      }
      case 'context': {
        // A page centred on one message replaces what the list held, because
        // the two are not contiguous and stitching them would put a silent
        // gap in the middle of a conversation. Failed rows survive it, as
        // they survive a welcome, for the same reason.
        patch(link, {
          hasEarlier: m.hasEarlier,
          ...keepNewest([...m.messages, ...link.view.messages.filter((r) => r.failed)]),
        });
        setHighlight(m.id);
        return;
      }
      case 'hidden': {
        // The row stays and the text goes, so a conversation somebody has
        // taken something out of has a visible gap rather than quietly
        // closing over one.
        //
        // `by` is absent from a room running a build from before authors
        // could delete their own, and every message such a room ever hid was
        // a moderator's — so the absence reads as `staff` rather than as
        // unknown, and the list has one less state to draw.
        const by = m.by ?? 'staff';
        patch(link, {
          messages: link.view.messages.map((r) =>
            // The quote on every reply goes with it, exactly as the room has
            // already done in its own table. A tab that kept showing the
            // words under an answer would be the one copy of them left
            // standing, which is the whole of what a deletion is for.
            r.id === m.id
              ? { ...r, hidden: true, hiddenBy: by, text: '' }
              : r.replyTo?.id === m.id
                ? { ...r, replyTo: { ...r.replyTo, text: '' } }
                : r,
          ),
        });
        return;
      }
      case 'edited': {
        // The words and the stamp, and nothing else. `ts` is untouched on
        // purpose: the row keeps its place in the conversation rather than
        // jumping to the bottom, because an edit is not a new message and a
        // list that reordered itself under a reader would lose them their
        // place for somebody else's typo.
        patch(link, {
          messages: link.view.messages.map((r) =>
            r.id === m.id
              ? { ...r, text: m.text, edited: m.edited }
              : r.replyTo?.id === m.id
                ? { ...r, replyTo: { ...r.replyTo, text: snippetOf(m.text) } }
                : r,
          ),
        });
        return;
      }
      case 'banned': {
        // The close is coming from the server; what matters is that this one
        // does not schedule a retry. Backing off into a door that is shut is
        // the one case where the reconnect loop is wrong, and it would be
        // wrong for as long as the ban lasts.
        link.done = true;
        patch(link, { status: 'idle', banned: { until: m.until, reason: m.reason }, you: null, members: [] });
        notifyRef.current(m.reason ?? reason('banned', 'You cannot post in this room.'), 'error');
        return;
      }
      case 'err': {
        if (m.nonce) {
          // The one failure worth a toast: your own words did not land.
          fail(link, m.nonce);
          notifyRef.current(reason(m.code, m.message), 'error');
          return;
        }
        switch (m.code) {
          case 'full':
            // The close that follows schedules the retry; this just names
            // the reason in the footer.
            patch(link, { full: true });
            return;
          case 'bad-search':
            setSearching(null);
            searchRef.current = null;
            notifyRef.current(reason(m.code, m.message), 'error');
            return;
          case 'bad-reaction':
          case 'bad-target':
          case 'not-yours':
            // None of these is anything the reader did wrong in a way they
            // could act on: a target that has been trimmed away under them, a
            // room whose pins are full, or a message whose author this tab
            // stopped being when somebody signed in on it. The server's own
            // sentence says which, and `reason` puts the last one into the
            // reader's language.
            notifyRef.current(reason(m.code, m.message), 'error');
            return;
          case 'bad-name': {
            notifyRef.current(reason(m.code, m.message), 'error');
            // Almost always a stored name meeting a rule that has since
            // tightened. The hello was refused, so this socket has no
            // member and will be sent nothing until it says hello again:
            // drop the name and do exactly that, with none. The panel goes
            // back to the prompt and the room keeps arriving meanwhile,
            // which is where a browser with no name starts anyway.
            //
            // A rename refused on a welcomed socket is the other half, and
            // there is nothing to repair: cleanName decided the same thing
            // on this side before the frame went out, so arriving here means
            // the two ends disagree about the rule. Say so, and leave the
            // name that is working where it is.
            if (link.view.status === 'open' || meRef.current.name === null) return;
            rename(null);
            // The name is one person's, not one socket's, so every socket
            // that has not been welcomed yet is holding the same refused
            // name and has to say hello again without it.
            for (const other of links.current.values()) if (other.view.status !== 'open') sayHello(other);
            return;
          }
          default:
            console.warn(`chat: ${link.room}: ${m.code}: ${m.message}`);
            return;
        }
      }
    }
  };

  const sayHello = (link: Link) => {
    const me = meRef.current;
    sendOn(link, {
      t: 'hello',
      v: PROTOCOL_VERSION,
      id: identityRef.current,
      // Null, not an empty string: no name is a listener, and an
      // unusable name is an error the room should still report.
      name: me.name,
      avatar: me.wearing,
      rank: me.rank,
      level: me.level,
    });
  };

  const connectionEvents = useRef({ hello: sayHello, message: handle, patch });
  connectionEvents.current = { hello: sayHello, message: handle, patch };
  const connections = useMemo(() => createConnections({
    hello: link => connectionEvents.current.hello(link),
    message: (link, message) => connectionEvents.current.message(link, message),
    patch: (link, value) => connectionEvents.current.patch(link, value),
  }), []);
  const connect = connections.connect;
  const drop = (link: Link) => { connections.drop(link); links.current.delete(link.room); };

  /* ── Which rooms are open ────────────────────────────────────────────── */

  /**
   * The home room and, when it is earned, the level's.
   *
   * `chosen` is home whatever the level is doing, and that is the change: the
   * level used to *replace* it. `roomFor` in lib/chat still decides which
   * room the level contributes — the rule is worth reading in one piece and
   * it is still the whole rule for a tab with one socket — but its caller now
   * adds rather than replaces. `follow` no longer moves anybody: it decides
   * whether the level gets to add its room at all, so pinning a room by hand
   * still means "the level stops deciding for me" and the picker's "This
   * level" still gives it back.
   */
  const levelId = follow && level !== null ? levelRoom(level) : null;
  const second = levelId !== null && levelId !== chosen;
  /**
   * Whether a second socket has been earned. `you` on any open socket is the
   * room's own answer to "may this person post" — see rule 4 — and being
   * signed in is the answer arriving before the question, since a gated room
   * cannot refuse a session and an ungated one cannot refuse a name.
   */
  const canPost = verified || views.some((v) => v.you !== null);
  /**
   * With one socket the tab is in one room, and `roomFor` is still the rule
   * for which — so a listener in a level is in the level's room, exactly as
   * every tab was before this change. With two, home is the room that was
   * picked and the level adds its own beside it.
   */
  const homeRoom = canPost ? chosen : roomFor(chosen, follow, level);
  const wantLevel = second && canPost;

  /**
   * Signing in or out, adopting a recovered identity, and a signed-in learner
   * renaming themselves are the changes that cannot be sent down a socket that
   * is already open: the session is read on the upgrade, the tag is hashed at
   * `hello`, and the name of a verified socket comes from the account row the
   * Worker read at the upgrade — a `me` frame from that socket is ignored, on
   * purpose (worker/room.ts). So every room goes and comes back, and
   * everything the old sockets knew goes with them — the unread counts least
   * of all, since they counted what was waiting for somebody else.
   *
   * Only the account's name is in here. A name picked on this browser changes
   * nothing about who the room thinks this is and travels as a `me` frame
   * ("A name picked in the panel or on the profile screen", below); putting it
   * here as well would close every socket over a word the room would have
   * taken anyway.
   */
  const generation = `${accountId ?? ''} ${account ? accountName(account.name) : ''} ${identity}`;
  const lastGeneration = useRef(generation);

  useEffect(() => {
    if (!wanted) return;
    const reset = lastGeneration.current !== generation;
    lastGeneration.current = generation;

    const want: Array<{ room: RoomId; isLevel: boolean }> = [{ room: homeRoom, isLevel: homeRoom === levelId }];
    if (wantLevel && levelId) want.push({ room: levelId, isLevel: true });
    // The protocol's own cap, restated here so that a third room can only
    // ever be a bug in this function rather than a socket in the wild.
    const desired = want.slice(0, LIMITS.open);
    const keep = new Set(desired.map((w) => w.room));
    // What this tab was already holding, read before anything is dropped. It
    // is what tells a room somebody has just walked into apart from one that
    // is only being reopened under a new identity, which look identical from
    // the other side of the loop below and mean opposite things on screen.
    const before = new Set(links.current.keys());

    let changed = false;
    for (const link of [...links.current.values()]) {
      if (!reset && keep.has(link.room)) continue;
      drop(link);
      changed = true;
    }
    // Kept apart because they answer different questions about what to show.
    // A level room that has just appeared is somebody walking into a level,
    // which is a request to see it; a home room that has just appeared may be
    // that, or it may be the home socket arriving late behind a level room
    // that was already on screen — which happens the moment a listener picks
    // a name and earns the second socket, and must not move them.
    let addedLevel: RoomId | null = null;
    let addedHome: RoomId | null = null;
    /** A room opened *because* of a permalink. It outranks both. */
    let followed: RoomId | null = null;
    for (const w of desired) {
      const link = links.current.get(w.room);
      if (link) {
        if (link.isLevel !== w.isLevel) {
          link.isLevel = w.isLevel;
          link.view = { ...link.view, isLevel: w.isLevel };
          changed = true;
        }
        continue;
      }
      const fresh: Link = {
        room: w.room,
        isLevel: w.isLevel,
        view: blankView(w.room, w.isLevel),
        ws: null,
        attempt: 0,
        retry: null,
        ping: null,
        done: false,
        // A permalink asked for while this room had no socket. Claimed here
        // rather than in goTo, because goTo is what caused this room to be
        // opened and the welcome is the first moment it can be asked for.
        wantContext: pendingGoTo.current?.room === w.room ? pendingGoTo.current.id : null,
      };
      if (fresh.wantContext !== null) {
        pendingGoTo.current = null;
        followed = w.room;
      }
      links.current.set(w.room, fresh);
      if (w.isLevel) addedLevel = w.room;
      else addedHome = w.room;
      changed = true;
      connect(fresh);
    }

    const ids = desired.map((w) => w.room);
    if (ids.join('|') !== order.current.join('|')) {
      order.current = ids;
      changed = true;
    }
    // In order: the room a link asked for, because somebody who clicked a
    // message is owed that message; a level room that has just opened,
    // because walking into a level is asking for its room; the room already
    // on screen, if it is still open, because nothing has happened that is
    // about it; and failing all of those, home.
    const shown = activeRef.current;
    const arrived = addedLevel !== null && !before.has(addedLevel) ? addedLevel : null;
    const next = followed ?? arrived ?? (shown && keep.has(shown) ? shown : (addedHome ?? ids[0] ?? null));
    if (next !== shown) {
      activeRef.current = next;
      setActiveRoom(next);
    }
    if (changed) publish();
    // The rooms, whether one is wanted at all, who is signed in and the id are
    // the only things that need new sockets: the room reads the session on the
    // upgrade and nowhere else, and the id is hashed into a tag at `hello` and
    // never revisited. The name, rank, level and objective ride along in refs
    // and are sent as `me` below — including a name picked for the first time,
    // which promotes a listener without reopening anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, homeRoom, levelId, wantLevel, generation, publish]);

  /**
   * The link is spent as soon as it has been used.
   *
   * Taken out of the URL here rather than when the page of messages arrives,
   * because the two failures are not symmetrical: a link left in the bar
   * sends the learner back to the same message on every refresh for the rest
   * of the visit, and a link cleared a second too early costs one refresh
   * that lands in the right room at the bottom of it. The flash is set now
   * and set again when the page arrives; whichever is later is the one that
   * is still showing when the row is on screen.
   */
  useEffect(() => {
    const link = linked.current;
    if (!link) return;
    setHighlight(link.id);
    clearLink();
  }, []);

  /** Every socket goes when the hook does, and only then. */
  useEffect(() => {
    const all = links.current;
    return () => {
      for (const link of [...all.values()]) drop(link);
      order.current = [];
      activeRef.current = null;
    };
  }, []);

  /* ── Coming back ─────────────────────────────────────────────────────── */

  useEffect(() => {
    const kick = () => {
      // Only a connection that is waiting out its backoff is worth kicking; a
      // socket mid-handshake will report on itself soon enough, and a room
      // that closed the door is not going to open it because the tab came
      // back into view.
      for (const link of links.current.values()) if (!link.done && link.view.status === 'offline') connect(link);
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      kick();
      if (openRef.current) {
        const link = current();
        if (link) readNow(link);
      }
      void pullInbox(false);
    };
    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('online', kick);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── What the room shows for you ─────────────────────────────────────── */

  useEffect(() => {
    for (const link of links.current.values()) {
      if (link.view.status !== 'open') continue;
      sendOn(link, { t: 'me', rank, level, objective });
      if (link.view.you) patch(link, { you: { ...link.view.you, rank, level, objective } });
    }
  }, [rank, level, objective, sendOn, patch]);

  /**
   * A figure saved on the profile screen, carried into every open room.
   *
   * Nothing optimistic: the room answers with `you`, and what it decides is
   * what the footer and the member list then show. It fires on mount as well,
   * which is one redundant frame on a socket that has just said hello with the
   * same value — cheaper than a condition that has to know whether hello has
   * been answered yet.
   */
  useEffect(() => {
    if (!wearing) return;
    for (const link of links.current.values()) {
      if (link.view.status === 'open') sendOn(link, { t: 'me', avatar: wearing });
    }
  }, [wearing, sendOn]);

  /**
   * A name picked in the panel or on the profile screen, carried into every
   * open room.
   *
   * Only for somebody who is not signed in: an account's name is read off the
   * session at the upgrade and the room ignores the frame, so sending one
   * would be a request that quietly does nothing. What a signed-in rename does
   * instead is change `account.name`, which changes `generation` above, which
   * closes the sockets and opens them again — the only way a room can be told,
   * because it asked the Worker rather than the browser.
   *
   * Unlike the figure, this one patches `you` on the way past. A rename is a
   * direct manipulation of a word that is on screen in the footer, and the
   * room's answer comes back a round trip later; the figure had no such word
   * to leave stale.
   */
  useEffect(() => {
    if (verified || !storedName) return;
    for (const link of links.current.values()) {
      if (link.view.status !== 'open') continue;
      sendOn(link, { t: 'me', name: storedName });
      // A listener has no `you` to patch — it has never been told its tag —
      // and gets one from the `you` frame the room sends back, which is also
      // what lets it post.
      if (link.view.you) patch(link, { you: { ...link.view.you, name: storedName } });
    }
  }, [storedName, verified, sendOn, patch]);

  // Opening the drawer on a visible tab is reading it — the room it is
  // showing, and only that one. The other room is open, not on screen, and
  // its count is the reason it is worth holding.
  useEffect(() => {
    if (!open || document.visibilityState !== 'visible') return;
    const link = current();
    if (link) readNow(link);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeRoom]);

  /* ── The cleared level ───────────────────────────────────────────────── */

  /**
   * The clock as one string, so "the same clear" is one comparison. Seeded
   * from the record at the first render rather than from null: `lastClear`
   * on a page that has just loaded is something that happened before this tab
   * existed, and announcing it would make every reload an announcement.
   */
  const clockKey = (p: Progress) => {
    const c = p.lastClear;
    return c ? `${c.missionId ?? ''}|${c.wall}|${c.mono}|${c.origin}` : '';
  };
  const announced = useRef(clockKey(progress));

  useEffect(() => {
    const key = clockKey(progress);
    if (key === announced.current) return;
    announced.current = key;
    const clock = progress.lastClear;
    if (!clock?.missionId) return;
    const lvl = missionLevel(clock.missionId);
    if (lvl === null) return;
    const done = progress.levels[clock.missionId];
    // Completed and vouched for, both. An unverified clear is still a clear
    // for the learner's own record (docs/cheating.md), but the one claim this
    // product makes about a room is that what the server says happened did:
    // the Worker refuses this frame from a socket it cannot name for the same
    // reason, and the tab should not be sending what the server would refuse.
    if (!done?.completed || !done.verified) return;
    pendingClear.current = { level: lvl, stars: done.stars };
    flushClear();
  }, [progress, flushClear]);

  /* ── Where everybody is ──────────────────────────────────────────────── */

  const occupancyAt = useRef(0);
  const refreshOccupancy = useCallback(() => {
    const now = Date.now();
    if (now - occupancyAt.current < OCCUPANCY_GAP_MS) return;
    occupancyAt.current = now;
    void fetchOccupancy().then((rooms) => setOccupancy(rooms));
  }, []);

  /* ── The API ─────────────────────────────────────────────────────────── */

  const setName = useCallback(
    (raw: string): boolean => {
      // A signed-in name is the account's; the server would ignore this, and
      // the profile screen renames one through the account instead
      // (state/useAccount.ts). The rooms are told by the effect above, which
      // is the same frame this used to send itself and now also covers a
      // rename that happened on the other screen.
      if (verified) return false;
      return rename(raw) !== null;
    },
    [verified],
  );

  const setFace = useCallback(
    (next: string | null): boolean => {
      // Null is a real choice — "give me back the one my id gives me" — so it
      // is the one value that skips the check.
      const clean = next === null ? null : cleanFace(next);
      if (next !== null && clean === null) return false;
      updatePrefs({ face: clean });
      setFaceState(clean);
      // Wearing a face is not wearing an operator, so the figure comes off —
      // here and on the account, or the next load would pull it straight back
      // down over the face that was just chosen. Started, not awaited: the
      // request only settles the account's copy, and this browser's is already
      // decided. Nothing else is needed to move the sockets either, because
      // taking the operator off changes `wearing`, and the effect above sends
      // the frame.
      void wearFace(verified);
      return true;
    },
    [verified],
  );

  const join = useCallback((next: RoomId) => {
    const from = order.current[0];
    // Picking a room by hand pins it: the level stops adding its own, here and
    // on every level after this one, until `followLevel` gives it back. Done
    // even when the room picked is the one already showing, because following
    // into it and choosing it are different intentions with the same address.
    const list = !from || next === from ? chatPrefs().recent : withRecent(withRecent(chatPrefs().recent, from), next);
    updatePrefs({ room: next, recent: list, follow: false });
    setRecent(list);
    setFollow(false);
    setChosen(next);
  }, []);

  const followLevel = useCallback(() => {
    // The level's own room is not a choice of room, so it does not join the
    // recent list: that list is for getting back to somewhere you picked, and
    // the level room is one screen away for as long as you are on the level.
    updatePrefs({ follow: true });
    setFollow(true);
  }, []);

  const show = useCallback(
    (room: RoomId) => {
      const link = links.current.get(room);
      if (!link || activeRef.current === room) return;
      activeRef.current = room;
      setActiveRoom(room);
      // A result set belongs to the room it was asked of, and so does a flash.
      searchRef.current = null;
      setSearching(null);
      setHighlight(null);
      if (openRef.current && document.visibilityState === 'visible') readNow(link);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const send = useCallback(
    (text: string, opts?: { replyTo?: number; mentions?: string[]; image?: ChatImage }): boolean => {
      const clean = cleanText(text);
      // Words or a picture, and not necessarily both. Before pictures this was
      // simply "empty is nothing to send"; a message that is a screenshot with
      // no caption is not empty, and refusing it with the rule about how long
      // a message may be would be answering a question nobody asked.
      if (!clean && !opts?.image) {
        notifyRef.current(getStrings().chat.rules.text(), 'error');
        return false;
      }
      const link = current();
      if (!link || link.view.status !== 'open') return false;
      const me = link.view.you;
      if (!me) return false;
      // Never yourself: a mention is a way to reach somebody else, and the
      // Worker drops your own tag anyway. Dropped here too so the optimistic
      // row and the echo agree about what was said.
      const mentions = cleanTags(opts?.mentions ?? []).filter((t) => t !== me.tag);
      // The parent as this tab happens to have it. A `replyTo` naming a
      // message that has scrolled out of these 400 rows is still sent — the
      // server has a thousand and answers `bad-target` only if it truly has
      // none — and the quote simply arrives with the echo instead of before
      // it, which is a slower reply rather than a broken one.
      const parent = opts?.replyTo === undefined ? undefined : link.view.messages.find((r) => r.id === opts.replyTo);
      const nonce = makeNonce();
      const row: ChatRow = {
        id: tempId(),
        ts: Date.now(),
        tag: me.tag,
        name: me.name,
        rank: me.rank,
        level: me.level,
        text: clean ?? '',
        verified: me.verified,
        avatar: me.avatar,
        nonce,
        pending: true,
        // Drawn straight away rather than waiting for the echo, and it is the
        // one optimistic field here that cannot be wrong: the bytes are
        // already in the bucket — the upload finished before this was called —
        // so the `<img>` resolves against a URL that already answers. What the
        // echo may still refuse is the *attachment*, and that shows up as the
        // row failing, which is what a failed row already looks like.
        ...(opts?.image ? { image: opts.image } : {}),
        ...(mentions.length ? { mentions } : {}),
        ...(parent ? { replyTo: { id: parent.id, tag: parent.tag, name: parent.name, text: snippetOf(parent.text) } } : {}),
      };
      patch(link, keepNewest([...link.view.messages, row]));
      const said: ClientMessage = {
        t: 'say',
        text: clean ?? '',
        nonce,
        ...(opts?.replyTo === undefined ? {} : { replyTo: opts.replyTo }),
        ...(mentions.length ? { mentions } : {}),
        ...(opts?.image ? { image: opts.image.id } : {}),
      };
      if (!sendOn(link, said)) fail(link, nonce);
      return true;
    },
    [current, patch, sendOn, fail],
  );

  const retry = useCallback(
    (nonce: string) => {
      // The row belongs to the room it was typed in, which is not necessarily
      // the one on screen: a failed line stays where it was said, and pressing
      // its retry from the room strip should still send it there.
      for (const link of links.current.values()) {
        const row = link.view.messages.find((r) => r.nonce === nonce && r.failed);
        if (!row) continue;
        if (link.view.status !== 'open') return;
        // A fresh nonce, so an echo of the first attempt that arrives late can
        // never be mistaken for the answer to this one.
        const fresh = makeNonce();
        patch(
          link,
          keepNewest([
            ...link.view.messages.filter((r) => r.nonce !== nonce),
            { ...row, id: tempId(), ts: Date.now(), nonce: fresh, failed: false, pending: true },
          ]),
        );
        const said: ClientMessage = {
          t: 'say',
          text: row.text,
          nonce: fresh,
          ...(row.replyTo ? { replyTo: row.replyTo.id } : {}),
          ...(row.mentions?.length ? { mentions: row.mentions } : {}),
          // Read back off the row rather than remembered anywhere, like every
          // other field here. A retry that dropped the picture would send the
          // caption on its own, which is a different message from the one
          // somebody pressed retry on — and for a message that was only ever
          // a picture it would be an empty one the room refuses.
          ...(row.image ? { image: row.image.id } : {}),
        };
        if (!sendOn(link, said)) fail(link, fresh);
        return;
      }
    },
    [patch, sendOn, fail],
  );

  /**
   * A reaction is not drawn until the room has broadcast it, and that is the
   * decision rather than an omission.
   *
   * Everywhere else in this file the optimistic row is the right answer,
   * because a message the server refuses can be marked "not sent" and handed
   * back to the person who typed it. A reaction has nowhere to be handed
   * back to: the server answers with the whole bucket and nothing else, so a
   * refused one — a face this build knows and that one does not, a listener
   * pressing it, a message trimmed away a second ago — would leave a count on
   * screen that is simply wrong and that nothing will ever correct. The round
   * trip is one hop to a Durable Object the socket is already attached to.
   */
  const react = useCallback(
    (id: number, emoji: string, on: boolean) => {
      const link = current();
      if (link) sendOn(link, { t: 'react', id, emoji, on });
    },
    [current, sendOn],
  );

  const pin = useCallback(
    (id: number, on: boolean) => {
      const link = current();
      if (link) sendOn(link, { t: 'pin', id, on });
    },
    [current, sendOn],
  );

  /**
   * New words, and no optimism about them.
   *
   * The same argument `react` makes, arrived at from the other end. The room
   * broadcasts `edited` to every socket including the one that asked, so the
   * text on screen a hop later is the text the room accepted — and the three
   * ways this can be refused (not yours, already gone, too fast) all leave the
   * message as it was, which is the right thing to be looking at. Painting the
   * new words first would mean a refusal has to put the old ones back, and a
   * message that silently reverted under a reader is worse than one that took
   * a moment.
   *
   * Cleaned here as well as on the wire, so a composer that would be refused
   * says so in the reader's own language rather than waiting for the Worker to
   * say it in English.
   */
  const edit = useCallback(
    (id: number, raw: string) => {
      const link = current();
      if (!link || id <= 0) return false;
      const text = cleanText(raw);
      if (!text) {
        notifyRef.current(reason('bad-text', ''), 'error');
        return false;
      }
      return sendOn(link, { t: 'edit', id, text });
    },
    [current, sendOn],
  );

  /**
   * Taken back, and not drawn as gone until the room says so — for the reason
   * above, and one more: this is the irreversible one. A row that vanished
   * optimistically and then came back because the socket was down would be the
   * panel telling somebody their message was gone when it was not.
   */
  const remove = useCallback(
    (id: number) => {
      const link = current();
      if (link && id > 0) sendOn(link, { t: 'delete', id });
    },
    [current, sendOn],
  );

  const report = useCallback(
    (id: number, why?: string) => {
      const link = current();
      // No acknowledgement to wait for and none to give: the room answers a
      // report with silence, because the only honest acknowledgement would be
      // a promise about a human.
      if (link) sendOn(link, { t: 'report', id, ...(why ? { reason: why } : {}) });
    },
    [current, sendOn],
  );

  const earlier = useCallback(() => {
    const link = current();
    if (!link || link.view.status !== 'open' || link.view.loadingEarlier) return;
    let before = Infinity;
    for (const r of link.view.messages) if (r.id > 0 && r.id < before) before = r.id;
    if (!Number.isFinite(before)) return;
    if (sendOn(link, { t: 'earlier', before })) patch(link, { loadingEarlier: true });
  }, [current, patch, sendOn]);

  const markRead = useCallback(() => {
    const link = current();
    if (link) readNow(link);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const search = useCallback(
    (raw: string) => {
      const link = current();
      if (!link) return;
      const q = cleanQuery(raw);
      if (!q) {
        // The tab says no sooner than the room would, and says it in the
        // reader's own language rather than in the Worker's.
        notifyRef.current(reason('bad-search', ''), 'error');
        return;
      }
      searchRef.current = { room: link.room, q };
      setSearching({ q, results: [], loading: true });
      if (!sendOn(link, { t: 'search', q })) {
        searchRef.current = null;
        setSearching(null);
      }
    },
    [current, sendOn],
  );

  const clearSearch = useCallback(() => {
    searchRef.current = null;
    setSearching(null);
  }, []);

  const goTo = useCallback(
    (room: RoomId, id: number) => {
      const link = links.current.get(room);
      if (link) {
        show(room);
        setHighlight(id);
        // The page centred on it, which is what makes a permalink land with
        // the conversation around it rather than at the bottom of a list the
        // message may not even be in.
        if (!sendOn(link, { t: 'context', id })) link.wantContext = id;
        return;
      }
      // A room this tab is not holding — a mention from the inbox, most
      // likely. There is no third socket to open, so the room becomes home:
      // the same thing typing its number does, with the page request queued
      // for the welcome. It does cost the level room, because pinning a room
      // by hand is what turns following off; following a link out of a level
      // is a deliberate act and the picker's "This level" is one press away.
      pendingGoTo.current = { room, id };
      setHighlight(id);
      join(room);
    },
    [join, show, sendOn],
  );

  // The flash is one event, not a state somebody has to clear. Held long
  // enough to find the row after the list has scrolled to it, then let go, so
  // that navigating to the same message twice flashes twice.
  useEffect(() => {
    if (highlight === null) return;
    const t = window.setTimeout(() => setHighlight(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(t);
  }, [highlight]);

  const block = useCallback((tag: string, on: boolean) => {
    const clean = cleanTag(tag);
    if (!clean) return;
    const now = chatPrefs().blocked;
    const next = on ? (now.includes(clean) ? now : [clean, ...now]) : now.filter((t) => t !== clean);
    if (next.length === now.length && on === now.includes(clean)) return;
    updatePrefs({ blocked: next });
    setBlocked(next);
  }, []);

  /* ── What the panel sees ─────────────────────────────────────────────── */

  const active = useMemo(() => {
    const shown = views.find((v) => v.room === activeRoom);
    // The fallback is a room with a socket that has not said anything yet,
    // which is exactly what the first paint is: `active` is never null,
    // because a panel that has to check would be a panel with two renderings
    // of the same empty room.
    return shown ?? views[0] ?? blankView(homeRoom, homeRoom === levelId);
  }, [views, activeRoom, homeRoom, levelId]);

  return {
    active,
    rooms: views,
    show,
    name,
    verified,
    face,
    operator,
    setName,
    setFace,
    recent,
    // Following is only true where it is also visible: on the level list there
    // is no level to follow, and the picker should not claim otherwise.
    following: follow && level !== null,
    join,
    followLevel,
    occupancy,
    refreshOccupancy,
    send,
    retry,
    react,
    pin,
    report,
    edit,
    remove,
    earlier,
    markRead,
    search,
    searching,
    clearSearch,
    goTo,
    highlight,
    inbox,
    markInboxSeen,
    notifyMentions,
    setNotifyMentions,
    blocked,
    block,
  };
}
